"use client";

import React, { useState, useEffect, useRef } from "react";
import { Search, User, Menu, X, Loader } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { ProductsSearch, useWPQueryForSearch } from "@/components/shop/ProductsSearch";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { EcommerceProductsProvider, useEcommerceProducts } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { cn } from "@/lib/utils";
interface MenuItem {
  id: string;
  label: string;
  href?: string;
  children?: MenuItem[];
}
function SearchResults() {
  const {
    products,
    loading,
    error
  } = useEcommerceProducts();
  if (loading) {
    return <div data-section-id="20758" className="flex items-center justify-center py-8 text-primary">
        <Loader className="h-6 w-6 animate-spin" />
      </div>;
  }
  if (error) {
    return <p className="py-4 text-sm text-destructive">Failed to load menu items.</p>;
  }
  if (!products.length) {
    return <p className="py-4 text-sm text-muted-foreground">No dishes found.</p>;
  }
  return <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {products.slice(0, 4).map(product => <Link key={product.id} to={product.link} className="group flex flex-col gap-2 rounded-lg border border-border bg-card p-3 transition-colors hover:border-primary hover:bg-muted/50">
          {product.featured_image_url && <div className="aspect-square w-full overflow-hidden bg-muted">
              <img src={product.featured_image_url} alt={product.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
            </div>}
          <div className="mt-1">
            <h4 className="font-sans text-sm font-black uppercase tracking-tighter text-foreground">
              {product.title}
            </h4>
            {product.price !== undefined && <p className="mt-1 font-mono text-xs font-bold text-primary">${product.price}</p>}
          </div>
        </Link>)}
    </div>;
}
function SearchWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [wp_query, hasSearchQuery] = useWPQueryForSearch(searchValue);
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  return <div ref={containerRef} className="relative">
      <Button variant="ghost" size="icon" onClick={() => setIsOpen(!isOpen)} className="text-foreground hover:bg-muted hover:text-primary" aria-label="Toggle search">
        <Search className="size-5" />
      </Button>

      {isOpen && <div className="absolute right-0 top-full mt-2 w-[90vw] max-w-3xl origin-top-right border border-border bg-background p-6 shadow-2xl sm:w-[600px] md:right-[-100px] lg:right-0">
          <EcommerceProductsProvider wp_query={wp_query} postType="product">
            <div className="space-y-6">
              <div className="relative">
                <ProductsSearch value={searchValue} onChange={setSearchValue} placeholder="FIND YOUR FLAVOR..." className="w-full [&_input]:h-12 [&_input]:rounded-none [&_input]:border-primary [&_input]:bg-muted/30 [&_input]:font-sans [&_input]:text-lg [&_input]:font-bold [&_input]:text-foreground [&_input]:placeholder:text-muted-foreground [&_input]:focus:ring-primary" />
              </div>
              
              <div className="min-h-[100px]">
                {!hasSearchQuery ? <p className="text-sm text-muted-foreground font-sans font-bold uppercase tracking-widest">
                    Hungry? Type to search...
                  </p> : <SearchResults />}
              </div>
            </div>
          </EcommerceProductsProvider>
        </div>}
    </div>;
}
function DesktopMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden h-10 items-center gap-8 md:flex">
        {[1, 2, 3].map(i => <div key={i} className="h-4 w-16 animate-pulse bg-muted" />)}
      </div>;
  }
  return <NavigationMenu className="hidden md:flex">
      <NavigationMenuList className="gap-8">
        {menuItems.map(item => <NavigationMenuItem key={item.id}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative py-2 font-sans text-sm font-black uppercase tracking-tighter text-foreground transition-colors hover:text-primary">
                {item.label}
                <span className="absolute -bottom-1 left-1/2 h-1.5 w-1.5 -translate-x-1/2 rounded-full bg-primary opacity-0 transition-all duration-300 group-hover:opacity-100" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenu({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 p-8">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-6 w-32 animate-pulse bg-muted" />)}
      </div>;
  }
  return <nav className="flex flex-col items-center gap-8 p-12">
      {menuItems.map(item => <Link key={item.id} to={item.href || "#"} onClick={onClose} className="font-sans text-3xl font-black uppercase tracking-tighter text-foreground transition-colors hover:text-primary">
          {item.label}
        </Link>)}
      <div className="mt-8 h-px w-12 bg-border" />
      <Link to="#" onClick={onClose} className="font-sans text-lg font-bold uppercase tracking-wider text-muted-foreground hover:text-primary">
        FIND US
      </Link>
    </nav>;
}
export default function PrimaryNavigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="main-navigation" className={cn("fixed top-0 z-50 w-full border-b transition-all duration-300", isScrolled ? "bg-background/70 border-border backdrop-blur-md" : "bg-background border-border")}>
      <div className="container mx-auto flex h-20 items-center justify-between px-4 md:px-8">
        {}
        <div className="flex items-center gap-12">
          <Link to="/GreatTaipeiPage" className="flex items-center">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1540096/img/great_taipei_logo.png" alt="Great Taipei 大台北" className="h-10 w-auto object-contain" />
          </Link>

          <MenuProvider menu_id="32">
            <DesktopMenu />
          </MenuProvider>
        </div>

        {}
        <div className="flex items-center gap-4 md:gap-6">
          {}
          <SearchWidget />

          {}
          <Link to="#" className="hidden text-foreground transition-colors hover:text-primary md:block" aria-label="My Account">
            <User className="size-5" />
          </Link>

          {}

          {}
          <Button variant="ghost" size="icon" className="text-foreground hover:bg-muted hover:text-primary md:hidden" onClick={() => setIsMobileMenuOpen(true)}>
            <Menu className="size-6" />
          </Button>
        </div>
      </div>

      {}
      {isMobileMenuOpen && <div className="fixed inset-0 z-50 flex flex-col bg-background text-foreground animate-in fade-in duration-200">
          <div className="flex h-20 items-center justify-between border-b border-border px-4">
            <Link to="/GreatTaipeiPage" onClick={() => setIsMobileMenuOpen(false)}>
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1540096/img/great_taipei_logo.png" alt="Great Taipei 大台北" className="h-10 w-auto object-contain" />
            </Link>
            <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(false)} className="text-foreground hover:bg-muted">
              <X className="size-6" />
            </Button>
          </div>
          <div className="flex-1 overflow-y-auto">
            <MenuProvider menu_id="32">
              <MobileMenu onClose={() => setIsMobileMenuOpen(false)} />
            </MenuProvider>
          </div>
        </div>}
    </header>;
}